import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface CompatibilityData {
  score: number; // 0-100
  sharedInterests: string[];
  totalShared: number;
}

interface CompatibilityMap {
  [userId: string]: CompatibilityData;
}

export const useMatchCompatibility = (otherUserIds: string[]) => {
  const { user } = useAuthContext();
  const [compatibilityData, setCompatibilityData] = useState<CompatibilityMap>({});
  const [loading, setLoading] = useState(true);

  const fetchCompatibility = useCallback(async () => {
    if (!user || otherUserIds.length === 0) {
      setCompatibilityData({});
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Get current user's interests
      const { data: myInterests, error: myError } = await supabase
        .from("user_interests")
        .select("interest_id, interests(name)")
        .eq("user_id", user.id);

      if (myError) throw myError;

      const myInterestIds = new Set(myInterests?.map((i) => i.interest_id) || []);
      const myInterestNames = new Map(
        myInterests?.map((i) => [i.interest_id, (i.interests as any)?.name]) || []
      );

      if (myInterestIds.size === 0) {
        // No interests set, return empty compatibility
        const emptyData: CompatibilityMap = {};
        otherUserIds.forEach((id) => {
          emptyData[id] = { score: 0, sharedInterests: [], totalShared: 0 };
        });
        setCompatibilityData(emptyData);
        setLoading(false);
        return;
      }

      // Get other users' interests
      const { data: otherInterests, error: otherError } = await supabase
        .from("user_interests")
        .select("user_id, interest_id")
        .in("user_id", otherUserIds);

      if (otherError) throw otherError;

      // Group interests by user
      const userInterestsMap: Record<string, Set<string>> = {};
      otherUserIds.forEach((id) => {
        userInterestsMap[id] = new Set();
      });

      otherInterests?.forEach((item) => {
        if (userInterestsMap[item.user_id]) {
          userInterestsMap[item.user_id].add(item.interest_id);
        }
      });

      // Calculate Jaccard similarity for each user
      const result: CompatibilityMap = {};
      
      otherUserIds.forEach((userId) => {
        const theirInterests = userInterestsMap[userId] || new Set();
        
        // Find intersection
        const shared: string[] = [];
        myInterestIds.forEach((id) => {
          if (theirInterests.has(id)) {
            const name = myInterestNames.get(id);
            if (name) shared.push(name);
          }
        });

        // Jaccard: intersection / union
        const union = new Set([...myInterestIds, ...theirInterests]);
        const score = union.size > 0 ? Math.round((shared.length / union.size) * 100) : 0;

        result[userId] = {
          score,
          sharedInterests: shared.slice(0, 5), // Top 5
          totalShared: shared.length,
        };
      });

      setCompatibilityData(result);
    } catch (error) {
      console.error("Error calculating compatibility:", error);
    } finally {
      setLoading(false);
    }
  }, [user, otherUserIds.join(",")]);

  useEffect(() => {
    fetchCompatibility();
  }, [fetchCompatibility]);

  const getCompatibility = useCallback(
    (userId: string): CompatibilityData => {
      return compatibilityData[userId] || { score: 0, sharedInterests: [], totalShared: 0 };
    },
    [compatibilityData]
  );

  return {
    compatibilityData,
    getCompatibility,
    loading,
    refetch: fetchCompatibility,
  };
};
